/**
 * Transport-level retry for the status-page API.
 *
 * The staff API is WAF-gated to office IPs and sees little traffic, so its
 * Lambda scales to zero. Opening the dashboard fires a burst of concurrent
 * staff requests into a cold function, and some of those cold starts surface
 * as 503s. Retrying absorbs that blip.
 *
 * Only 5xx and outright network failures are retried. A 403 is returned
 * untouched on the first attempt: that is the WAF's "not staff" answer, and
 * callers depend on receiving it promptly to fall back to the public view.
 * Retrying it would delay every non-staff page load for nothing.
 */

export interface RetryOpts {
  /** Total tries, including the first. Default 3. */
  attempts?: number;
  /** Delay before the first retry; doubles each time. Default 150ms. */
  baseDelayMs?: number;
  /** Injectable for tests so they don't pay real backoff. */
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));

/**
 * Runs `doFetch`, retrying transient failures with exponential backoff.
 *
 * Returns the first response that isn't a 5xx, or the last response once
 * attempts are exhausted — so callers keep seeing a real Response and their
 * existing ok/status handling is unchanged. Network rejections are retried
 * too, and rethrown if every attempt fails.
 */
export async function fetchWithRetry(
  doFetch: () => Promise<Response>,
  opts: RetryOpts = {},
): Promise<Response> {
  const attempts = opts.attempts ?? 3;
  const baseDelayMs = opts.baseDelayMs ?? 150;
  const sleep = opts.sleep ?? defaultSleep;

  let lastErr: unknown;
  for (let i = 0; i < attempts; i += 1) {
    if (i > 0) await sleep(baseDelayMs * 2 ** (i - 1));
    try {
      const res = await doFetch();
      if (res.status < 500 || i === attempts - 1) return res;
    } catch (err) {
      lastErr = err;
      if (i === attempts - 1) throw err;
    }
  }
  throw lastErr;
}
